import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../config/axiosInstance";

export const fetchProducts = createAsyncThunk(
  "products/fetchProducts",
  async (
    { country, categoryId, collection, gender, tags, search, isTopCollection, priceMax, priceMin, page = 1, limit = 12, type },
    thunkAPI
  ) => {
    try {
      const queryParams = new URLSearchParams();
      if (country) queryParams.append("country", country);
      if (categoryId) queryParams.append("categoryId", categoryId);
      if (gender) queryParams.append("gender", gender);
      if (tags) queryParams.append("tags", tags); // comma separated
      if (collection) queryParams.append("collection", collection); // comma separated
      if (search) queryParams.append("search", search);
      if (priceMin) queryParams.append("priceMin", priceMin);
      if (priceMax) queryParams.append("priceMax", priceMax);
      if (isTopCollection) queryParams.append("isTopCollection", isTopCollection);
      if (type) queryParams.append("type", type);
      queryParams.append("page", page);
      queryParams.append("limit", limit);

      const response = await axiosInstance.get(
        `/api/products?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch products"
      );
    }
  }
);

export const fetchProductById = createAsyncThunk(
  "products/fetchById",
  async ({ id }, thunkAPI) => {
    try {
      const { data } = await axiosInstance.get(`/api/products/${id}`);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch product"
      );
    }
  }
);

const productSlice = createSlice({
  name: "products",
  initialState: {
    items: [],
    productDetail: null,
    total: 0,
    loading: false,
    error: null,
    page: 1,
  },
  reducers: {
    clearProductDetail: (state) => {
      state.productDetail = null;
      state.error = null; // Also clear error when clearing product detail
    },
    clearProductError: (state) => {
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        // Only show loading for initial fetch, not for similar products
        if (state.items.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.products || action.payload;
        state.total = action.payload.total || state.items.length;
        state.page = action.payload.page || 1;
        state.error = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Product by ID
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.productDetail = action.payload;
        state.error = null;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || "Failed to fetch product";
        state.productDetail = null;
      });
  },
});

export const { clearProductDetail, clearProductError, setLoading } = productSlice.actions;

// Selectors
export const selectProducts = (state) => state.products.items;
export const selectProductDetail = (state) => state.products.productDetail;
export const selectProductsLoading = (state) => state.products.loading;
export const selectProductsError = (state) => state.products.error;
export const selectProductsTotal = (state) => state.products.total;
export const selectProductsPage = (state) => state.products.page;

export default productSlice.reducer;